<template>
  <div class="enquete-wrapper" :style="wrapperStyles" :key="item.id">
    <!-- Title Card -->
    <div v-if="!hideTitle" class="title-card" :style="titleCardStyles">
      <img
        src="@/assets/AngelicaAoVivo/Enquetes/goticula-verde.png"
        alt=""
        class="goticula"
        :style="goticulaVerdeStyles"
      />
      <img
        src="@/assets/AngelicaAoVivo/Enquetes/goticula-bege.png"
        alt=""
        class="goticula"
        :style="goticulaBegeStyles"
      />

      <span class="title-text" :style="titleStyles">
        {{ item.dsc_titulo_enquete }}
      </span>
    </div>

    <img
      v-if="!hideTitle"
      src="@/assets/AngelicaAoVivo/Enquetes/goticula-rosa.png"
      alt=""
      class="goticula"
      :style="goticulaRosaStyles"
    />

    <!-- Results Card -->
    <div class="results-card" :style="resultsCardStyles">
      <!-- Poll Content Section -->
      <div class="poll-content" :style="pollContentStyles">
      <!-- Unified Progress Bar with Labels and Percentages Inside -->
      <transition name="results-transition" mode="out-in">
        <!-- Multi-option Results (for 3+ options) -->
        <div v-if="item.flg_exibir_resultado && hasMultipleOptions" key="with-multi-results" class="multi-options-results" :style="multiOptionsResultsStyles">
          <div
            v-for="(opcao, index) in item.enquete_opcoes"
            :key="opcao.id"
            class="multi-option-item"
            :style="multiOptionItemStyles(index)"
          >
            <div class="multi-option-content" :style="multiOptionContentStyles">
              <span class="multi-option-percentage" :style="multiOptionPercentageStyles">
                {{ animatedPercentages[index] || 0 }}%
              </span>
              <span v-if="!hideOptionTitle" class="multi-option-label" :style="multiOptionLabelStyles">
                {{ opcao.dsc_titulo }}
              </span>
            </div>
          </div>
        </div>

        <!-- Unified Progress Bar (for 2 options only) -->
        <div v-else-if="item.flg_exibir_resultado && !hasMultipleOptions" key="with-results" class="unified-progress-container" :style="unifiedProgressContainerStyles">
          <!-- Unified Progress Bar -->
          <div class="unified-progress-bar" :style="unifiedProgressBarStyles">
            <!-- Left Progress Fill -->
            <div class="progress-fill-left" :style="progressFillLeftStyles"></div>
            <!-- Right Progress Fill -->
            <div class="progress-fill-right" :style="progressFillRightStyles"></div>

            <!-- Content overlay (labels and percentages inside the bar) -->
            <div class="progress-overlay-content" :style="progressOverlayContentStyles">
              <!-- Left side: Label + Percentage -->
              <div class="progress-content-left" :style="progressContentLeftStyles">
                <transition name="percentage-fade" appear>
                  <span class="percentage-inside" :style="percentageInsideStyles">
                    {{ animatedLeftPercentage }}%
                  </span>
                </transition>
                <span v-if="!hideOptionTitle" class="option-label-inside" :style="optionLabelInsideStyles">
                  {{ leftOption.dsc_titulo }}
                </span>
              </div>

              <!-- Right side: Percentage + Label -->
              <div class="progress-content-right" :style="progressContentRightStyles">
                <transition name="percentage-fade" appear>
                  <span class="percentage-inside" :style="percentageInsideStyles">
                    {{ animatedRightPercentage }}%
                  </span>
                </transition>
                <span v-if="!hideOptionTitle" class="option-label-inside" :style="optionLabelInsideStyles">
                  {{ rightOption.dsc_titulo }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Options display when no results -->
        <div v-else key="without-results" class="options-container" :style="optionsContainerStyles">
          <div
            v-for="opcao in item.enquete_opcoes"
            :key="opcao.id"
            :style="optionButtonStyles"
          >
            <span v-if="!hideOptionTitle" class="option-text" :style="optionTextStyles">
              {{ opcao.dsc_titulo }}
            </span>
          </div>
        </div>
      </transition>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    item: Object,
    posicaoEnquete: String,
    hideTitle: Boolean,
    hideOptionTitle: Boolean,
    isVerticalBar: Boolean,
    customGoticulaPositions: {
      type: Object,
      default: null
    },
  },

  data() {
    return {
      AngelicaTheme: {
        colors: {
          cardBg: "#FFFFFF",
          titleColor: "#1A1A1A",
          labelColor: "#000000",
          percentageColor: "#632418",
          progressBar: "#c979bb",
          progressBg: "#F5F5F5",
          headerFooterBg: "#E0E0E0",
          gradientStart: "#D4669C",
          gradientEnd: "#F4A6C8",
        },
        typography: {
          titleFont: "MozaicGeo Regular",
          labelFont: "MozaicGeo Bold",
        },
      },
      goticulaPositions: {
        normal: {
          verde: {
            top: -43.88,
            left: -2.16,
            width: 7.82,
            zIndex: 10,
          },
          bege: {
            top: -58.16,
            right: 0.16,
            width: 8.89,
            zIndex: 2,
          },
          rosa: {
            bottom: 40.53,
            right: 11.2,
            width: 32.11,
            zIndex: 0,
          },
        },
        expanded: {
          verde: {
            top: -43.88,
            left: -1.16,
            width: 5.82,
            zIndex: 10,
          },
          bege: {
            top: -58.16,
            right: 0.16,
            width: 6.7,
            zIndex: 2,
          },
          rosa: {
            bottom: 40.53,
            right: -1.2,
            width: 32.11,
            zIndex: 0,
          },
        },
      },
      animatedLeftPercentage: 0,
      animatedRightPercentage: 0,
      animatedProgressLeft: 0,
      animatedProgressRight: 0,
      animatedPercentages: [],
      animationDuration: 1800,
      previousResultsVisible: false,
    };
  },

  computed: {
    screenWidth() {
      return this.$vuetify.breakpoint.width;
    },

    screenHeight() {
      return this.$vuetify.breakpoint.height;
    },

    responsiveScale() {
      const baseWidth = 1920;
      const baseHeight = 1080;
      const widthScale = this.screenWidth / baseWidth;
      const heightScale = this.screenHeight / baseHeight;
      const scale = Math.min(widthScale, heightScale);
      return Math.max(0.5, Math.min(1.2, scale));
    },

    responsiveLayout() {
      const scale = this.responsiveScale;

      return {
        cardWidth: Math.round(1500 * scale),
        titleCardHeight: Math.round(98 * scale),
        resultsCardHeight: Math.round(160 * scale),
        titleFontSize: Math.round(28 * scale),
        labelFontSize: Math.round(30 * scale),
        percentageFontSize: Math.round(45 * scale),
        progressBarHeight: Math.round(90 * scale),
        percentageWidth: Math.round(140 * scale),
        gap: Math.round(20 * scale),
        margin: Math.round(50 * scale),
        titleTopOffset: Math.round(50 * scale),
        padding: Math.round(40 * scale),
      };
    },

    currentTheme() {
      return this.AngelicaTheme;
    },

    leftOption() {
      return this.item?.enquete_opcoes?.[0] || { dsc_titulo: '', resultado: 0 };
    },

    rightOption() {
      return this.item?.enquete_opcoes?.[1] || { dsc_titulo: '', resultado: 0 };
    },

    hasMultipleOptions() {
      return this.item?.enquete_opcoes?.length > 2;
    },

    needsExpansion() {
      if (!this.hasMultipleOptions || !this.item?.flg_exibir_resultado) {
        return false;
      }

      const optionCount = this.item?.enquete_opcoes?.length || 0;

      if (optionCount === 3) {
        const hasLongLabel = this.item.enquete_opcoes.some(
          opcao => (opcao.dsc_titulo || '').length > 15
        );
        return hasLongLabel;
      }
      return optionCount >= 4;
    },

    layoutState() {
      return this.needsExpansion ? 'expanded' : 'normal';
    },

    winningOptionIndex() {
      if (!this.item?.enquete_opcoes || this.item.enquete_opcoes.length === 0) {
        return -1;
      }

      let maxIndex = 0;
      let maxPercentage = this.item.enquete_opcoes[0]?.resultado || 0;

      this.item.enquete_opcoes.forEach((opcao, index) => {
        if ((opcao.resultado || 0) > maxPercentage) {
          maxPercentage = opcao.resultado || 0;
          maxIndex = index;
        }
      });

      return maxIndex;
    },

    dynamicTitleCardWidth() {
      const isCombinedMode = !!this.customGoticulaPositions;

      // Only apply smaller sizing when results are displayed in combined mode
      if (isCombinedMode && this.item?.flg_exibir_resultado && this.needsExpansion) {
        return 85; // Reduced for 4+ options with results + QR code
      }

      if (this.needsExpansion) {
        return 100;
      }
      return 75;
    },

    dynamicResultsCardWidth() {
      const isCombinedMode = !!this.customGoticulaPositions;

      if (this.hasMultipleOptions && !this.item?.flg_exibir_resultado) {
        return 60;
      }

      // Only apply smaller sizing when results are displayed in combined mode
      if (isCombinedMode && this.item?.flg_exibir_resultado && this.needsExpansion) {
        return 75; // Reduced for 4+ options with results + QR code
      }

      if (this.needsExpansion) {
        return 88;
      }
      return 63;
    },

    multiOptionScaleFactor() {
      const isCombinedMode = !!this.customGoticulaPositions;

      // Only apply more aggressive scaling when results are displayed in combined mode
      if (isCombinedMode && this.item?.flg_exibir_resultado) {
        if (this.needsExpansion) {
          return 0.65; // More reduction for 4+ options with results + QR code
        }
        return 0.85; // Slight reduction for 3 options with results + QR code
      }

      if (this.needsExpansion) {
        return 0.75;
      }
      return 1;
    },

    activeGoticulaPositions() {
      if (this.customGoticulaPositions) {
        return this.customGoticulaPositions;
      }
      return this.goticulaPositions;
    },

    activeLayoutState() {
      return this.layoutState;
    },

    goticulaVerdeStyles() {
      const pos = this.activeGoticulaPositions[this.activeLayoutState].verde;
      return {
        top: `${pos.top}%`,
        left: `${pos.left}%`,
        width: `${pos.width}%`,
        height: 'auto',
        zIndex: pos.zIndex,
        transition: 'top 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, left 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, right 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, bottom 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s',
      };
    },

    goticulaBegeStyles() {
      const pos = this.activeGoticulaPositions[this.activeLayoutState].bege;
      return {
        top: `${pos.top}%`,
        right: `${pos.right}%`,
        width: `${pos.width}%`,
        height: 'auto',
        zIndex: pos.zIndex,
        transition: 'top 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, left 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, right 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, bottom 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s',
      };
    },

    goticulaRosaStyles() {
      const pos = this.activeGoticulaPositions[this.activeLayoutState].rosa;
      return {
        bottom: `${pos.bottom}%`,
        right: `${pos.right}%`,
        width: `${pos.width}%`,
        height: 'auto',
        zIndex: pos.zIndex,
        transition: 'top 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, left 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, right 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, bottom 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s',
      };
    },

    wrapperStyles() {
      const position = this.cardPositionStyles;
      return `${position}; position: relative; width: ${this.responsiveLayout.cardWidth}px; display: flex; flex-direction: column; gap: ${Math.round(15 * this.responsiveScale)}px;`;
    },

    cardPositionStyles() {
      const positionMap = {
        esquerda: `justify-self: flex-start; margin-left: ${this.responsiveLayout.margin}px;`,
        centro: "justify-self: center",
        direita: `justify-self: flex-end; margin-right: ${this.responsiveLayout.margin}px;`,
      };
      return positionMap[this.posicaoEnquete] || "";
    },

    titleCardStyles() {
      return {
        width: `${this.dynamicTitleCardWidth}%`,
        minHeight: `${this.responsiveLayout.titleCardHeight}px`,
        backgroundColor: this.currentTheme.colors.cardBg,
        borderRadius: "37px",
        border: "1px solid #E5E5E5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        zIndex: 1,
        padding: `${Math.round(15 * this.responsiveScale)}px ${Math.round(30 * this.responsiveScale)}px`,
        transition: "width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      };
    },

    resultsCardStyles() {
      return {
        width: `${this.dynamicResultsCardWidth}%`,
        borderRadius: "90px",
        position: "relative",
        zIndex: 1,
        transition: "width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      };
    },

    titleStyles() {
      return {
        color: this.currentTheme.colors.titleColor,
        fontFamily: `${this.currentTheme.typography.titleFont} !important`,
        fontSize: `${this.responsiveLayout.titleFontSize}px`,
        fontWeight: "700",
        lineHeight: "1.2",
        display: "block",
        textAlign: "center",
        whiteSpace: "normal",
        wordWrap: "break-word",
        overflowWrap: "break-word",
        maxWidth: "100%",
      };
    },

    pollContentStyles() {
      return {
        width: "100%",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      };
    },

    unifiedProgressContainerStyles() {
      return {
        display: "flex",
        alignItems: "center",
        width: "100%",
      };
    },

    unifiedProgressBarStyles() {
      return {
        width: "100%",
        height: `${this.responsiveLayout.progressBarHeight}px`,
        backgroundColor: this.currentTheme.colors.progressBg,
        borderRadius: "30px",
        overflow: "visible",
        position: "relative",
        display: "flex",
      };
    },

    progressFillLeftStyles() {
      return {
        width: `${this.animatedProgressLeft}%`,
        height: "100%",
        background: this.animatedProgressLeft > 0 ? this.currentTheme.colors.gradientStart : 'transparent',
        transition: "width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), background 0.8s ease",
        borderRadius: "30px 0 0 30px",
      };
    },

    progressFillRightStyles() {
      return {
        width: `${this.animatedProgressRight}%`,
        height: "100%",
        background: this.animatedProgressRight > 0 ? this.currentTheme.colors.progressBg : 'transparent',
        transition: "width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), background 0.8s ease",
        marginLeft: "auto",
        borderRadius: "0 30px 30px 0",
      };
    },

    progressOverlayContentStyles() {
      return {
        position: "absolute",
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: `0 ${Math.round(20 * this.responsiveScale)}px`,
        pointerEvents: "none",
        zIndex: 2,
      };
    },

    progressContentLeftStyles() {
      return {
        display: "flex",
        alignItems: "center",
        gap: `${Math.round(15 * this.responsiveScale)}px`,
      };
    },

    progressContentRightStyles() {
      return {
        display: "flex",
        alignItems: "center",
        gap: `${Math.round(15 * this.responsiveScale)}px`,
      };
    },

    optionLabelInsideStyles() {
      return {
        color: `${this.currentTheme.colors.labelColor}`,
        fontFamily: `${this.currentTheme.typography.labelFont} !important`,
        fontSize: `${this.responsiveLayout.labelFontSize}px`,
        fontWeight: "500",
        whiteSpace: "nowrap",
      };
    },

    percentageInsideStyles() {
      return {
        color: `${this.currentTheme.colors.percentageColor}`,
        fontFamily: `${this.currentTheme.typography.labelFont} !important`,
        fontSize: `${this.responsiveLayout.percentageFontSize}px`,
        fontWeight: "700",
        whiteSpace: "nowrap",
        transition: "all 0.3s ease-out",
      };
    },

    optionsContainerStyles() {
      return {
        display: "flex",
        gap: `${Math.round(20 * this.responsiveScale)}px`,
        width: "90%", // Increased from 80% to give more space to option buttons
        justifyContent: "center",
        alignItems: "center",
      };
    },

    optionButtonStyles() {
      const optionCount = this.item?.enquete_opcoes?.length || 0;
      let minWidth = "35%";

      if (optionCount === 3) {
        const maxLength = Math.max(
          ...this.item.enquete_opcoes.map(opcao => (opcao.dsc_titulo || '').length)
        );

        const calculatedWidth = Math.max(30, Math.min((maxLength * 2.1) + 5, 52));
        minWidth = `${calculatedWidth}%`;
      }

      return {
        flex: "0 1 auto",
        maxWidth: "45%",
        minWidth: minWidth,
        minHeight: `${this.responsiveLayout.progressBarHeight}px`,
        backgroundColor: this.currentTheme.colors.cardBg,
        borderRadius: "30px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.3s ease"
      };
    },

    optionTextStyles() {
      return {
        color: this.currentTheme.colors.labelColor,
        fontFamily: `${this.currentTheme.typography.labelFont} !important`,
        fontSize: `${this.responsiveLayout.labelFontSize}px`,
        fontWeight: "500",
        textAlign: "center",
        whiteSpace: "normal",
        wordWrap: "break-word",
      };
    },

    multiOptionsResultsStyles() {
      return {
        display: "flex",
        gap: `${Math.round(20 * this.responsiveScale)}px`,
        width: "80%",
        justifyContent: "center",
        alignItems: "center",
      };
    },

    multiOptionContentStyles() {
      return {
        display: "flex",
        alignItems: "center",
        gap: `${Math.round(20 * this.responsiveScale)}px`,
        width: "100%",
        height: "100%",
        overflow: "hidden",
      };
    },

    multiOptionPercentageStyles() {
      const scaledFontSize = Math.round(this.responsiveLayout.percentageFontSize * this.multiOptionScaleFactor);
      return {
        color: this.currentTheme.colors.percentageColor,
        fontFamily: `${this.currentTheme.typography.labelFont} !important`,
        fontSize: `${scaledFontSize}px`,
        fontWeight: "700",
        textAlign: "left",
        flexShrink: 0,
        whiteSpace: "nowrap",
      };
    },

    multiOptionLabelStyles() {
      const scaledFontSize = Math.round(this.responsiveLayout.labelFontSize * this.multiOptionScaleFactor);
      return {
        color: this.currentTheme.colors.labelColor,
        fontFamily: `${this.currentTheme.typography.labelFont} !important`,
        fontSize: `${scaledFontSize}px`,
        fontWeight: "500",
        flex: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
      };
    },
  },

  watch: {
    'item.flg_exibir_resultado': {
      handler(newVal) {
        if (newVal && !this.previousResultsVisible) {
          if (this.hasMultipleOptions) {
            this.item.enquete_opcoes.forEach((opcao, index) => {
              this.$set(this.animatedPercentages, index, 0);
              const staggerDelay = 150 + (index * 200);
              setTimeout(() => {
                this.$set(this.animatedPercentages, index, opcao.resultado || 0);
              }, staggerDelay);
            });
          } else {
            setTimeout(() => {
              this.animateValue('animatedLeftPercentage', 0, this.leftOption.resultado || 0);
              this.animateValue('animatedProgressLeft', 0, this.leftOption.resultado || 0);
            }, 150);
            setTimeout(() => {
              this.animateValue('animatedRightPercentage', 0, this.rightOption.resultado || 0);
              this.animateValue('animatedProgressRight', 0, this.rightOption.resultado || 0);
            }, 350);
          }
        }
        else if (!newVal) {
          if (this.hasMultipleOptions) {
            this.animatedPercentages = [];
          } else {
            this.animatedLeftPercentage = 0;
            this.animatedRightPercentage = 0;
            this.animatedProgressLeft = 0;
            this.animatedProgressRight = 0;
          }
        }
        this.previousResultsVisible = newVal;
      },
      immediate: true,
    },
    'leftOption.resultado': {
      handler(newVal, oldVal) {
        if (this.item.flg_exibir_resultado && this.previousResultsVisible) {
          this.animateValue('animatedLeftPercentage', oldVal || 0, newVal || 0);
          this.animateValue('animatedProgressLeft', oldVal || 0, newVal || 0);
        }
      },
    },
    'rightOption.resultado': {
      handler(newVal, oldVal) {
        if (this.item.flg_exibir_resultado && this.previousResultsVisible) {
          this.animateValue('animatedRightPercentage', oldVal || 0, newVal || 0);
          this.animateValue('animatedProgressRight', oldVal || 0, newVal || 0);
        }
      },
    },
  },

  methods: {
    multiOptionItemStyles(index) {
      const isWinner = index === this.winningOptionIndex;
      return {
        width: "100%",
        height: `${this.responsiveLayout.progressBarHeight}px`,
        minHeight: `${this.responsiveLayout.progressBarHeight}px`,
        maxHeight: `${this.responsiveLayout.progressBarHeight}px`,
        backgroundColor: isWinner ? this.currentTheme.colors.gradientStart : this.currentTheme.colors.cardBg,
        borderRadius: "37px",
        padding: `${Math.round(15 * this.responsiveScale)}px ${Math.round(25 * this.responsiveScale)}px`,
        display: "flex",
        alignItems: "center",
        transition: "background-color 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.3s ease",
      };
    },

    animateValue(propertyName, fromValue, toValue) {
      const startTime = Date.now();
      const difference = toValue - fromValue;

      if (this[propertyName + 'Animation']) {
        cancelAnimationFrame(this[propertyName + 'Animation']);
      }

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / this.animationDuration, 1);

        const easeOutQuart = 1 - Math.pow(1 - progress, 4);

        this[propertyName] = Math.round(fromValue + (difference * easeOutQuart));

        if (progress < 1) {
          this[propertyName + 'Animation'] = requestAnimationFrame(animate);
        } else {
          this[propertyName] = toValue;
          delete this[propertyName + 'Animation'];
        }
      };

      this[propertyName + 'Animation'] = requestAnimationFrame(animate);
    },
  },

  mounted() {
    if (this.item?.flg_exibir_resultado) {
      if (this.hasMultipleOptions) {
        this.item.enquete_opcoes.forEach((opcao, index) => {
          this.$set(this.animatedPercentages, index, opcao.resultado || 0);
        });
      } else {
        this.animatedLeftPercentage = this.leftOption.resultado || 0;
        this.animatedRightPercentage = this.rightOption.resultado || 0;
        this.animatedProgressLeft = this.leftOption.resultado || 0;
        this.animatedProgressRight = this.rightOption.resultado || 0;
      }
      this.previousResultsVisible = true;
    } else {
      this.animatedLeftPercentage = 0;
      this.animatedRightPercentage = 0;
      this.animatedProgressLeft = 0;
      this.animatedProgressRight = 0;
      this.animatedPercentages = [];
      this.previousResultsVisible = false;
    }
  },

  beforeDestroy() {
    Object.keys(this.$data).forEach(key => {
      if (key.endsWith('Animation') && this[key]) {
        cancelAnimationFrame(this[key]);
      }
    });
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "MozaicGeo Regular";
  src: url("@/assets/AngelicaAoVivo/fonts/MozaicGEO-Regular.otf") format("opentype");
}

@font-face {
  font-family: "MozaicGeo Bold";
  src: url("@/assets/AngelicaAoVivo/fonts/MozaicGEO-Bold.otf") format("opentype");
}

.enquete-wrapper {
  position: relative;
  align-items: center;
  padding-bottom: 50px;
}

.goticula {
  position: absolute;
  pointer-events: none;
}

.title-card {
  position: relative;
  transition: width 0.5s ease;
}

.results-card {
  position: relative;
  transition: width 0.5s ease;
}

.title-text {
  display: block;
  text-align: center;
}

.poll-content {
  width: 100%;
}

.unified-progress-container {
  display: flex;
  align-items: center;
}

.unified-progress-bar {
  display: flex;
  position: relative;
}

.progress-fill-left,
.progress-fill-right {
  position: relative;
}

.options-container {
  width: 100%;
}

.results-transition-enter-active,
.results-transition-leave-active {
  transition: all 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.results-transition-enter-from {
  opacity: 0;
  transform: translateY(15px) scale(0.96);
}

.results-transition-leave-to {
  opacity: 0;
  transform: translateY(-15px) scale(0.96);
}

.percentage-fade-enter-active {
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-delay: 0.3s;
}

.percentage-fade-enter-from {
  opacity: 0;
  transform: scale(0.7) translateY(10px);
}

@media (max-width: 768px) {
  .horizontal-poll-container {
    flex-direction: column;
    gap: 15px;
  }

  .option-container {
    width: 100%;
    min-width: 300px;
  }
}

@media (max-height: 600px) {
  .card {
    margin-bottom: 0;
  }
}
</style>
